import { INDICATOR_KEYS, type IndicatorKey, indicatorMeta } from "./indicators.js";

export type ConditionOp = "<" | "<=" | ">" | ">=";
export type ConditionWindow = "7d" | "30d";

/**
 * A single indicator threshold, e.g. "F&G < 30" or "USDT.D 7d > 0".
 * `window` set means "change over that window" (percentage points for pp indicators, percent otherwise).
 */
export type Condition = {
  readonly key: IndicatorKey;
  readonly window: ConditionWindow | null;
  readonly op: ConditionOp;
  readonly value: number;
  /** "przez N dni": the condition must hold on each of the last N daily values (null/undefined = just now) */
  readonly days?: number | null;
};

const ALIASES: Record<string, IndicatorKey> = {
  "f&g": "fng",
  fng: "fng",
  fear: "fng",
  "fear&greed": "fng",
  "btc.d": "btc_dominance",
  btcd: "btc_dominance",
  btc_dominance: "btc_dominance",
  "eth.d": "eth_dominance",
  ethd: "eth_dominance",
  eth_dominance: "eth_dominance",
  "usdt.d": "stable_dominance",
  "usdc.d": "stable_dominance",
  "stable.d": "stable_dominance",
  "usdt.d+usdc.d": "stable_dominance",
  stable_dominance: "stable_dominance",
  total3: "total3_usd",
  total3_usd: "total3_usd",
  btc: "btc_usd",
  btc_usd: "btc_usd",
  "eth/btc": "eth_btc",
  ethbtc: "eth_btc",
  eth_btc: "eth_btc",
  asi: "alt_season",
  alt: "alt_season",
  altseason: "alt_season",
  alt_season: "alt_season",
  "200w": "btc_200w",
  btc_200w: "btc_200w",
  sma200w: "btc_200w",
  pi: "btc_pi_cycle",
  picycle: "btc_pi_cycle",
  pi_cycle: "btc_pi_cycle",
  btc_pi_cycle: "btc_pi_cycle",
  mvrv: "btc_mvrv",
  btc_mvrv: "btc_mvrv",
  halving: "halving_days",
  halving_days: "halving_days",
};

const OPS: readonly ConditionOp[] = ["<=", ">=", "<", ">"];

function compareOp(op: ConditionOp, x: number, value: number): boolean {
  switch (op) {
    case "<":
      return x < value;
    case "<=":
      return x <= value;
    case ">":
      return x > value;
    case ">=":
      return x >= value;
  }
}

/**
 * Parses free text like "F&G < 30", "BTC.D >= 55 %", "USDT.D 7d > 0", "F&G > 80 przez 7 dni".
 * Returns null when unparseable, or when both a window (7d/30d) and a "przez N dni" persistence
 * are given — the two are mutually exclusive.
 */
export function parseCondition(text: string): Condition | null {
  const src = text.trim().toLowerCase().replace(/\s+/g, " ");
  const m =
    /^([a-z0-9&./_+]+)(?: (7d|30d))? ?(<=|>=|<|>|=<|=>) ?(-?\d+(?:[.,]\d+)?) ?(?:%|pp|usd)?(?: przez (\d+) ?d(?:ni)?)?$/i.exec(
      src,
    );
  if (!m) return null;
  const [, rawKey, rawWindow, rawOp, rawValue, rawDays] = m;
  const key = rawKey ? ALIASES[rawKey] : undefined;
  if (!key || !rawValue) return null;
  const op = (rawOp === "=<" ? "<=" : rawOp === "=>" ? ">=" : rawOp) as ConditionOp;
  if (!OPS.includes(op)) return null;
  const value = Number.parseFloat(rawValue.replace(",", "."));
  if (!Number.isFinite(value)) return null;
  const window = rawWindow === "7d" || rawWindow === "30d" ? rawWindow : null;
  if (rawDays) {
    if (window) return null; // window + persistence is not a valid combination
    const days = Number.parseInt(rawDays, 10);
    if (!Number.isInteger(days) || days < 2 || days > 30) return null;
    return { key, window, op, value, days };
  }
  return { key, window, op, value };
}

export function formatCondition(c: Condition): string {
  const meta = indicatorMeta(c.key);
  const suffix = c.window ? (meta.isPp ? " pp" : " %") : meta.key.endsWith("dominance") ? " %" : "";
  const daysSuffix = c.days ? ` przez ${c.days} dni` : "";
  return `${meta.short}${c.window ? ` ${c.window}` : ""} ${c.op} ${c.value}${suffix}${daysSuffix}`;
}

export type IndicatorValues = Partial<
  Record<
    IndicatorKey,
    {
      value: number;
      change7d: number | null;
      change30d: number | null;
      /** daily values, oldest first, ≤30 points — needed to evaluate "przez N dni" persistence */
      series?: readonly number[];
    }
  >
>;

/** Returns null when the indicator (or its window change / persistence series) is unavailable. */
export function evaluateCondition(c: Condition, values: IndicatorValues): boolean | null {
  const v = values[c.key];
  if (!v) return null;
  if (c.days != null) {
    if (c.window) return null; // window + persistence is not a valid combination
    const series = v.series;
    if (!series || series.length < c.days) return null;
    return series.slice(-c.days).every((x) => Number.isFinite(x) && compareOp(c.op, x, c.value));
  }
  const x = c.window === "7d" ? v.change7d : c.window === "30d" ? v.change30d : v.value;
  if (x == null || !Number.isFinite(x)) return null;
  return compareOp(c.op, x, c.value);
}

export function isIndicatorKey(s: string): s is IndicatorKey {
  return (INDICATOR_KEYS as readonly string[]).includes(s);
}
